import { useCallback } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import { useSlate, useFocused } from 'slate-react';
import gettext from '@sdk/lang/gettext';
import BodyPortal from '@sdk/common/tooltip/BodyPortal';
import AttachmentsPickerButton from '@sdk/attachments/components/AttachmentsPickerButton';
import { UseAttachmentsType } from '@sdk/attachments/hooks/useAttachments';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import Button from '@sdk/components/ui/buttons/Button';
import Row from '@sdk/components/ui/spacing/Row';
import { ModalState } from '@sdk/components/ui/modal/useModalState';

import BlockQuoteSvg from '@web/assets/svg/UI/block-quote.svg';
import BlockQuoteActiveSvg from '@web/assets/svg/UI/block-quote-active.svg';
import { LinkModalState } from '@web/richText/TextEditorInner/hooks/useLinkModalState';
import { removeMark } from '@web/richText/helpers/utils';
import EditorToolbarButton from '@web/richText/components/EditorToolbarButton';

import TextFocusButton from './TextFocustButton';
import GifPickerButton from './GifPickerButton';
import EmojiPicker, { EmojiPickerPosition } from './emojiPicker/EmojiPicker';
import LinkModal from './linkModal/LinkModal';
import LinkToolbarButton from './LinkToolbarButton';
import MarkBlockIconButton from './MarkBlockIconButton';
import MarkBlockTextButton from './MarkBlockTextButton';
import MarkButton from './MarkButton';

type ContainerProps = {
  isFocused: boolean;
};
const ToolbarContainer = styled(Row)<ContainerProps>`
  align-items: center;
  background-color: ${({ theme, isFocused }): string =>
    isFocused ? theme.colors.bodyBg : theme.colors.white};
  border-top: 1px solid;
  border-color: ${({ theme }): string => theme.colors.separatorOnWhite};
  border-top-color: ${({ theme, isFocused }): string =>
    isFocused ? theme.colors.separatorOnWhite : 'transparent'};
  padding-right: 10px;
  padding-left: 10px;
  justify-content: space-between;
  transition: all 0.3s ease;
  min-height: 38px;
`;

const SubmitButtonContainer = styled(Row)`
  width: 94px;
  margin-top: 5px;
  margin-bottom: 4px;
`;

const OpacityFocusContainer = styled(Row)<ContainerProps>`
  opacity: ${({ isFocused }): number => (isFocused ? 1 : 0.3)};
  transition: all 0.3s ease;
`;

const CenteredRow = styled(Row)`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
  gap: 5px;
`;

type Props = {
  submitLabel?: string;
  submitActive: boolean;
  submit: () => void;
  focusWithSelection: () => void;
  insertText: (text: string) => void;
  emojiPickerPosition: EmojiPickerPosition;
  gifPickerEnabled?: boolean;
  gifPickerState: ModalState;
  emojiPickerState: ModalState;
  linkModalState: LinkModalState;
  blurEditor: () => void;
  hideSubmit?: boolean;
  attachmentsState: UseAttachmentsType;
  attachmentsDisabled?: boolean;
  hiddenToolbarControls?: boolean;
};

const EditorToolbar: React.FC<React.PropsWithChildren<Props>> = ({
  submitLabel,
  submit,
  submitActive,
  insertText,
  focusWithSelection,
  emojiPickerPosition,
  gifPickerEnabled,
  gifPickerState,
  emojiPickerState,
  linkModalState,
  blurEditor,
  hideSubmit,
  attachmentsState,
  attachmentsDisabled,
  hiddenToolbarControls,
}) => {
  const editor = useSlate();
  const isFocused = useFocused();
  const onDismiss = useCallback(
    (event?: Event): void => {
      event?.preventDefault();
      linkModalState.setUrl('');
      focusWithSelection();
      removeMark(editor, 'highlight');
    },
    [focusWithSelection, linkModalState, editor]
  );

  const onSubmit = useCallback(() => {
    linkModalState.setUrl('');
    focusWithSelection();
  }, [focusWithSelection, linkModalState]);

  const activateText = (): void => {
    gifPickerState.close();
    emojiPickerState.close();
    setTimeout(focusWithSelection);
  };

  const toggleGifs = (): void => {
    if (gifPickerState.isVisible) {
      activateText();
    } else {
      blurEditor();
      emojiPickerState.close();
      gifPickerState.open();
    }
  };

  const toggleEmojis = (): void => {
    if (emojiPickerState.isVisible) {
      activateText();
    } else {
      blurEditor();
      emojiPickerState.open();
      gifPickerState.close();
    }
  };

  const onAttachmentClick = (): void => {
    gifPickerState.close();
    emojiPickerState.close();
  };

  return (
    <ToolbarContainer
      isFocused={isFocused || linkModalState.modalState.isVisible}
      onMouseDown={(event): void => {
        if (isFocused) {
          event.preventDefault();
          event.nativeEvent.stopImmediatePropagation();
        }
      }}>
      <BodyPortal>
        <LinkModal
          initialUrl={linkModalState.url}
          modalState={linkModalState.modalState}
          onSubmit={onSubmit}
          modalPosition={linkModalState.linkModalPosition}
          onDismiss={onDismiss}
          isOpenedFromToolbar={linkModalState.isOpenedFromToolbar}
        />
      </BodyPortal>

      {!hiddenToolbarControls && (
        <OpacityFocusContainer isFocused={isFocused}>
          <MarkButton format="bold" semiBold>
            B
          </MarkButton>
          <MarkButton format="italic">/</MarkButton>
          <MarkButton format="strikethrough" textDecoration="line-through">
            &nbsp;S&nbsp;
          </MarkButton>
          <LinkToolbarButton
            modalState={linkModalState.modalState}
            onLinkClick={linkModalState.onLinkClick}
          />
          <MarkBlockTextButton format="heading-1">H</MarkBlockTextButton>
          <MarkBlockIconButton
            icon={<BlockQuoteSvg />}
            activeIcon={<BlockQuoteActiveSvg />}
            format="block-quote"
          />
        </OpacityFocusContainer>
      )}
      {hiddenToolbarControls && <Row />}
      <Row>
        {/* Hack for angular attachments */}
        <CenteredRow mRight={attachmentsDisabled ? 40 : 5}>
          {(gifPickerEnabled || !hiddenToolbarControls) && (
            <TextFocusButton
              isActive={isFocused && !gifPickerState.isVisible}
              activate={activateText}
            />
          )}

          {gifPickerEnabled && (
            <GifPickerButton isActive={gifPickerState.isVisible} toggle={toggleGifs} />
          )}
          <EmojiPicker
            pickerState={emojiPickerState}
            onSelect={insertText}
            pickerPosition={emojiPickerPosition}
            toggle={toggleEmojis}
          />
          {!attachmentsDisabled && (
            <EditorToolbarButton borderRadius={2}>
              <AttachmentsPickerButton
                onSelect={attachmentsState.addAttachmentsFromFilelist}
                onOpen={onAttachmentClick}
              />
            </EditorToolbarButton>
          )}
        </CenteredRow>

        {!hideSubmit && (
          <SubmitButtonContainer>
            <Button
              height={28}
              fontSize={12}
              fontWeight="semiBold"
              disabled={!submitActive}
              buttonType={ButtonType.primary}
              onClick={submit}>
              {submitLabel || gettext('Send')}
            </Button>
          </SubmitButtonContainer>
        )}
      </Row>
    </ToolbarContainer>
  );
};

export default EditorToolbar;
